import React, { useEffect, useState, useMemo } from "react";
import { Flower2, Plus, Search, Edit2, Save, X, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { EmptyState } from "@/components/EmptyState";
import { CardGridSkeleton } from "@/components/LoadingSkeleton";
import { ImageStager } from "@/components/ImageStager";
import { authFetch } from "@/lib/auth";
import { uploadAttachment } from "@/lib/attachments";
import { toast } from "sonner";
import type { Garden } from "@shared/types";
export function GardensPage() {
  const [gardens, setGardens] = useState<Garden[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Add form
  const [fName, setFName] = useState("");
  const [fSize, setFSize] = useState("");
  const [fType, setFType] = useState("");
  const [fStatus, setFStatus] = useState("active");
  const [fNotes, setFNotes] = useState("");
  // Staged image attached after the garden is created.
  const [imageFile, setImageFile] = useState<File | null>(null);
  // Edit state
  const [editId, setEditId] = useState<string | null>(null);
  const [eName, setEName] = useState("");
  const [eSize, setESize] = useState("");
  const [eType, setEType] = useState("");
  const [eStatus, setEStatus] = useState("active");
  const [eNotes, setENotes] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const fetchGardens = async () => {
    try {
      const data = await authFetch<{ items: Garden[] }>("/api/gardens");
      setGardens(data.items ?? []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      console.error("[Gardens]", msg);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchGardens();
  }, []);
  const statuses = useMemo(() => {
    const s = new Set(gardens.map((g) => g.status).filter(Boolean));
    return Array.from(s).sort();
  }, [gardens]);
  const filtered = useMemo(() => {
    return gardens.filter((g) => {
      const ms = g.name.toLowerCase().includes(search.toLowerCase());
      const mst = statusFilter === "all" || g.status === statusFilter;
      return ms && mst;
    });
  }, [gardens, search, statusFilter]);
  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fName.trim()) { toast.error("Name is required"); return; }
    setSubmitting(true);
    try {
      const created = await authFetch<{ id: string }>("/api/gardens", {
        method: "POST",
        body: JSON.stringify({
          name: fName.trim(),
          size: fSize.trim(),
          type: fType.trim(),
          status: fStatus,
          notes: fNotes.trim(),
        }),
      });

      if (imageFile && created?.id) {
        try {
          await uploadAttachment(imageFile, {
            linked_entity_type: "garden",
            linked_entity_id: created.id,
          });
        } catch (uploadErr) {
          toast.warning(
            `Saved, but image upload failed: ${(uploadErr as Error).message ?? "unknown error"}`,
          );
        }
      }

      toast.success("Garden added");
      setFName(""); setFSize(""); setFType(""); setFStatus("active"); setFNotes(""); setImageFile(null);
      setDialogOpen(false);
      setLoading(true);
      fetchGardens();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to add");
    } finally {
      setSubmitting(false);
    }
  };
  const startEdit = (g: Garden) => {
    setEditId(g.id);
    setEName(g.name);
    setESize(g.size);
    setEType(g.type);
    setEStatus(g.status);
    setENotes(g.notes);
  };
  const handleEditSave = async () => {
    if (!editId) return;
    setEditSaving(true);
    try {
      await authFetch(`/api/gardens/${editId}`, {
        method: "PATCH",
        body: JSON.stringify({
          name: eName.trim(),
          size: eSize.trim(),
          type: eType.trim(),
          status: eStatus,
          notes: eNotes.trim(),
        }),
      });
      toast.success("Garden updated");
      setEditId(null);
      setLoading(true);
      fetchGardens();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to update");
    } finally {
      setEditSaving(false);
    }
  };
  const handleArchive = async (id: string) => {
    try {
      await authFetch(`/api/gardens/${id}`, { method: "DELETE" });
      toast.success("Garden archived");
      setGardens((prev) => prev.filter((g) => g.id !== id));
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to archive");
    }
  };
  return (
    <>
      <PageHeader title="Gardens" breadcrumbs={[{ label: "Dashboard", href: "/" }, { label: "Gardens" }]}>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90">
              <Plus className="h-4 w-4 mr-2" /> Add Garden
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle>Add Garden</DialogTitle></DialogHeader>
            <form onSubmit={handleAdd} className="space-y-4 mt-2">
              <div className="space-y-2"><Label>Name *</Label><Input value={fName} onChange={(e) => setFName(e.target.value)} placeholder="Kitchen Garden" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Size</Label><Input value={fSize} onChange={(e) => setFSize(e.target.value)} placeholder="0.25 acres" /></div>
                <div className="space-y-2"><Label>Type</Label><Input value={fType} onChange={(e) => setFType(e.target.value)} placeholder="Vegetable" /></div>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={fStatus} onValueChange={setFStatus}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="dormant">Dormant</SelectItem>
                    <SelectItem value="planning">Planning</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Notes</Label><Textarea value={fNotes} onChange={(e) => setFNotes(e.target.value)} rows={2} /></div>
              <div className="space-y-2">
                <Label>Image</Label>
                <ImageStager
                  file={imageFile}
                  onChange={setImageFile}
                  disabled={submitting}
                />
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90">
                {submitting ? "Adding..." : "Add Garden"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </PageHeader>
      {error && <div className="mb-4 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">{error}</div>}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
        <div className="relative flex-1 w-full sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search gardens..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-40"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {statuses.map((s) => (
              <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
            ))}
            {!statuses.includes("active") && <SelectItem value="active">Active</SelectItem>}
            {!statuses.includes("dormant") && <SelectItem value="dormant">Dormant</SelectItem>}
          </SelectContent>
        </Select>
      </div>
      {loading ? (
        <CardGridSkeleton count={4} />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Flower2}
          title="No gardens found"
          description={search || statusFilter !== "all" ? "Try adjusting your filters." : "Add your first garden to get started."}
          actionLabel={!search && statusFilter === "all" ? "Add Garden" : undefined}
          onAction={!search && statusFilter === "all" ? () => setDialogOpen(true) : undefined}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((g) => (
            <Card key={g.id} className="p-5 hover:shadow-md transition-shadow duration-200">
              {editId === g.id ? (
                <div className="space-y-3">
                  <Input value={eName} onChange={(e) => setEName(e.target.value)} placeholder="Name" />
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={eSize} onChange={(e) => setESize(e.target.value)} placeholder="Size" />
                    <Input value={eType} onChange={(e) => setEType(e.target.value)} placeholder="Type" />
                  </div>
                  <Select value={eStatus} onValueChange={setEStatus}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="dormant">Dormant</SelectItem>
                      <SelectItem value="planning">Planning</SelectItem>
                    </SelectContent>
                  </Select>
                  <Textarea value={eNotes} onChange={(e) => setENotes(e.target.value)} rows={2} placeholder="Notes" />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleEditSave} disabled={editSaving} className="bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90 flex-1">
                      <Save className="h-3 w-3 mr-1" /> {editSaving ? "Saving..." : "Save"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditId(null)}><X className="h-3 w-3" /></Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Flower2 className="h-5 w-5 text-ranch-green" />
                      <h3 className="font-semibold text-foreground">{g.name}</h3>
                    </div>
                    {g.status && <StatusBadge status={g.status} />}
                  </div>
                  <div className="text-sm text-muted-foreground space-y-0.5 mb-2">
                    {g.size && <p>Size: <span className="font-medium text-foreground">{g.size}</span></p>}
                    {g.type && <p>Type: <span className="font-medium text-foreground">{g.type}</span></p>}
                  </div>
                  {g.notes && <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{g.notes}</p>}
                  <div className="flex gap-2 pt-1 border-t">
                    <Button variant="ghost" size="sm" onClick={() => startEdit(g)} className="text-muted-foreground hover:text-foreground">
                      <Edit2 className="h-3.5 w-3.5 mr-1" /> Edit
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                          <Trash2 className="h-3.5 w-3.5 mr-1" /> Archive
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Archive {g.name}?</AlertDialogTitle><AlertDialogDescription>This will hide the garden from active lists.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => handleArchive(g.id)} className="bg-destructive text-destructive-foreground">Archive</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </>
              )}
            </Card>
          ))}
        </div>
      )}
    </>
  );
}
